import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GameComplete.css';

function GameComplete({
    victory = true,
    gameName = 'Game',
    score = 0,
    xpEarned = 0,
    coinsEarned = 0,
    stars = 0,
    accuracy = 0,
    stats = {},
    onPlayAgain,
    onNextLevel,
    onBackToMap,
    showNextLevel = false,
    topic = null  // Add topic prop for navigation
}) {
    const navigate = useNavigate();
    const [showRewards, setShowRewards] = useState(false);
    const [animatedXP, setAnimatedXP] = useState(0);
    const [animatedCoins, setAnimatedCoins] = useState(0);

    useEffect(() => {
        // Delay showing rewards for dramatic effect
        const timer = setTimeout(() => setShowRewards(true), 800);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!showRewards) return;

        // Animate XP counting up
        const xpDuration = 1500;
        const xpSteps = 30;
        const xpIncrement = xpEarned / xpSteps;
        let currentXP = 0;

        const xpInterval = setInterval(() => {
            currentXP += xpIncrement;
            if (currentXP >= xpEarned) {
                setAnimatedXP(xpEarned);
                clearInterval(xpInterval);
            } else {
                setAnimatedXP(Math.floor(currentXP));
            }
        }, xpDuration / xpSteps);

        // Animate coins counting up
        const coinsDuration = 1200;
        const coinsSteps = 20;
        const coinsIncrement = coinsEarned / coinsSteps;
        let currentCoins = 0;

        const coinsInterval = setInterval(() => {
            currentCoins += coinsIncrement;
            if (currentCoins >= coinsEarned) {
                setAnimatedCoins(coinsEarned);
                clearInterval(coinsInterval);
            } else {
                setAnimatedCoins(Math.floor(currentCoins));
            }
        }, coinsDuration / coinsSteps);

        return () => {
            clearInterval(xpInterval);
            clearInterval(coinsInterval);
        };
    }, [showRewards, xpEarned, coinsEarned]);

    const handleBackToMap = () => {
        if (onBackToMap) {
            onBackToMap();
        } else if (topic) {
            navigate('/world-map', { state: { topic } });
        } else {
            navigate('/home');
        }
    };

    return (
        <div className="game-complete-overlay">
            <div className={`game-complete-modal ${victory ? 'victory' : 'defeat'}`}>
                {/* Confetti/particles for victory */}
                {victory && (
                    <div className="victory-particles">
                        {[...Array(20)].map((_, i) => (
                            <span key={i} className="particle" style={{
                                '--delay': `${Math.random() * 2}s`,
                                '--x': `${Math.random() * 100}%`,
                                '--duration': `${2 + Math.random() * 2}s`
                            }}>
                                {['⭐', '✨', '🎉', '🌟', '💫'][Math.floor(Math.random() * 5)]}
                            </span>
                        ))}
                    </div>
                )}

                <div className="complete-header">
                    <span className="complete-icon">
                        {victory ? '🏆' : '💪'}
                    </span>
                    <h1 className="complete-title">
                        {victory ? 'Victory!' : 'Good Try!'}
                    </h1>
                    <p className="complete-subtitle">
                        {victory
                            ? `You conquered ${gameName}!`
                            : `Keep practicing - you'll get it!`
                        }
                    </p>
                </div>

                {/* Stars */}
                <div className="complete-stars">
                    {[1, 2, 3].map((star) => (
                        <span
                            key={star}
                            className={`star ${star <= stars ? 'earned' : 'empty'}`}
                            style={{ '--delay': `${0.2 + star * 0.2}s` }}
                        >
                            {star <= stars ? '⭐' : '☆'}
                        </span>
                    ))}
                </div>

                {/* Score */}
                <div className="complete-score">
                    <span className="score-label">Final Score</span>
                    <span className="score-value">{score.toLocaleString()}</span>
                </div>

                {/* Rewards */}
                {showRewards && (
                    <div className="complete-rewards">
                        <div className="reward-item xp">
                            <span className="reward-icon">⭐</span>
                            <span className="reward-value">+{animatedXP}</span>
                            <span className="reward-label">XP</span>
                        </div>
                        <div className="reward-item coins">
                            <span className="reward-icon">🪙</span>
                            <span className="reward-value">+{animatedCoins}</span>
                            <span className="reward-label">Coins</span>
                        </div>
                        {accuracy > 0 && (
                            <div className="reward-item accuracy">
                                <span className="reward-icon">🎯</span>
                                <span className="reward-value">{accuracy}%</span>
                                <span className="reward-label">Accuracy</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Stats */}
                {Object.keys(stats).length > 0 && (
                    <div className="complete-stats">
                        {stats.matches !== undefined && (
                            <div className="stat-chip">
                                <span>✨ {stats.matches} Matches</span>
                            </div>
                        )}
                        {stats.bestCombo !== undefined && (
                            <div className="stat-chip">
                                <span>🔥 {stats.bestCombo}x Best Combo</span>
                            </div>
                        )}
                        {stats.questionsAnswered !== undefined && (
                            <div className="stat-chip">
                                <span>❓ {stats.questionsAnswered} Questions</span>
                            </div>
                        )}
                        {stats.timeElapsed !== undefined && (
                            <div className="stat-chip">
                                <span>⏱️ {stats.timeElapsed}s</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="complete-actions">
                    {onPlayAgain && (
                        <button className="complete-btn play-again" onClick={onPlayAgain}>
                            <span>🔄</span> Play Again
                        </button>
                    )}
                    {showNextLevel && onNextLevel && (
                        <button className="complete-btn next-level" onClick={onNextLevel}>
                            <span>➡️</span> Next Level
                        </button>
                    )}
                    <button className="complete-btn back-to-map" onClick={handleBackToMap}>
                        <span>🗺️</span> Back to Map
                    </button>
                </div>
            </div>
        </div>
    );
}

export default GameComplete;
